import { Injectable } from '@angular/core';
import { isAddressSet } from '@core/util/address';
import { GetPrimaryTitle } from '@core/util/matter';
import { isSet } from '@core/util/value';
import { Jurisdiction } from '@models/jurisdictions';
import {
	Matter,
	MatterAdjustment,
	MatterAdjustmentType,
	PropertyStatus,
	MatterTitle,
} from '@models/matter';
import { MatterMortgage } from '@models/mortgage';
import { TitleInsurancePolicyType } from '@models/title-insurance';
import { StepMilestoneTask } from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';
import { UrlService } from 'src/app/shared/services/url/url.service';

@Injectable({
	providedIn: 'root',
})
export class StepsService {
	// ------------------------
	// MATTER
	// ------------------------

	fileNumber = (matter: Matter) =>
		this.buildTaskToComplete('File number', isSet(matter?.number));

	closingDate = (matter: Matter) =>
		this.buildTaskToComplete(
			'Closing date',
			isSet(matter?.closingDate),
			UrlService.Matter_APS(matter?.id),
		);

	propertyDetails_pid = (matter: Matter, matterTitles: MatterTitle[]) => {
		const primary = GetPrimaryTitle(matterTitles);

		return this.buildTaskToComplete(
			'PID',
			primary && isSet(primary?.pinNumber),
			UrlService.Matter_Property_Details(matter?.id),
		);
	};

	propertyDetails_rollNumber = (matter: Matter) =>
		this.buildTaskToComplete(
			'Assessment roll number',
			isSet(matter?.propertyDetails?.rollNumber),
			UrlService.Matter_Property_Details(matter?.id),
		);

	propertyDetails_legalDescription = (
		matter: Matter,
		matterTitles: MatterTitle[],
	) => {
		const primary = GetPrimaryTitle(matterTitles);

		return this.buildTaskToComplete(
			'Legal description',
			primary && isSet(primary?.legalDescription),
			UrlService.Matter_Property_Details(matter?.id),
		);
	};

	propertyDetails_propertyAddress = (matter: Matter) =>
		this.buildTaskToComplete(
			'Property address',
			isAddressSet(matter?.propertyDetails?.address),
			UrlService.Matter_APS(matter?.id),
		);

	propertyDetails_jurisdiction = (
		jurisdiction: Jurisdiction,
		matterId: string,
	) =>
		this.buildTaskToComplete(
			'Jurisdiction',
			isSet(jurisdiction?.name),
			UrlService.Matter_Property_Details(matterId),
		);

	propertyDetails_condoOrStrat = (matter: Matter) =>
		this.buildTaskToComplete(
			'Condo or strat',
			isSet(matter?.propertyDetails?.propertyType?.type),
			UrlService.Matter_Property_Details(matter?.id),
		);

	propertyDetails_typeOfEstate = (matter: Matter) =>
		this.buildTaskToComplete(
			'Type of estate',
			isSet(matter?.propertyDetails?.interestEstate),
			UrlService.Matter_Property_Details(matter?.id),
		);

	propertyDetails_newConstruction = (
		matter: Matter,
		adjustments: MatterAdjustment[],
		items: StepMilestoneTask[],
	) => {
		if (matter?.propertyDetails?.propertyType?.status === PropertyStatus.NEW) {
			const newHomeWarrantyExists =
				adjustments?.findIndex(
					(x) => x.type === MatterAdjustmentType.TarionWarranty,
				) > -1;
			items.push(
				this.buildTaskToComplete(
					'New home warranty',
					newHomeWarrantyExists,
					UrlService.Matter_Adjustments(matter?.id),
				),
			);
		}
	};

	propertyDetails_lro = (matter: Matter) =>
		this.buildTaskToComplete(
			'Registry office / land title office',
			isSet(matter?.propertyDetails?.lro?.selected?.id),
			UrlService.Matter_Property_Details(matter?.id),
		);

	// ------------------------
	// TITLE INSURANCE
	// ------------------------

	titleInsurance_companyName = (matter: Matter) =>
		this.buildTaskToComplete(
			'Company name',
			isSet(matter?.titleInsurance?.companyName),
			UrlService.Matter_TitleInsurance(matter?.id),
		);

	titleInsurance_policyDate = (effectiveDate: string, matterId: string) =>
		this.buildTaskToComplete(
			'Title insurance submitted',
			isSet(effectiveDate),
			UrlService.Matter_TitleInsurance(matterId),
		);

	titleInsurance_policyNumber = (matter: Matter) =>
		this.buildTaskToComplete(
			'Lender Policy number',
			isSet(
				matter?.titleInsurance?.policies?.find(
					(x) => x.type === TitleInsurancePolicyType.LENDER,
				)?.number,
			),
		);

	// ------------------------
	// TITLE INSURANCE
	// ------------------------

	fireInsurance_companyName = (matter: Matter) =>
		this.buildTaskToComplete(
			'Company name',
			isSet(matter?.fireInsurance?.fireInsuranceCompany?.name),
			UrlService.Matter_Property_FireInsurance(matter?.id),
		);

	fireInsurance_companyAddress = (matter: Matter) =>
		this.buildTaskToComplete(
			'Company address',
			isAddressSet(matter?.fireInsurance?.fireInsuranceCompany?.address),
			UrlService.Matter_Property_FireInsurance(matter?.id),
		);

	fireInsurance_expiryDate = (matter: Matter) =>
		this.buildTaskToComplete(
			'Expiry date',
			isSet(matter?.fireInsurance?.expiryDate),
			UrlService.Matter_Property_FireInsurance(matter?.id),
		);

	fireInsurance_policyNumber = (matter: Matter) =>
		this.buildTaskToComplete(
			'Policy number',
			isSet(matter?.fireInsurance?.policyNumber),
			UrlService.Matter_Property_FireInsurance(matter?.id),
		);

	fireInsurance_insuranceAmount = (matter: Matter) =>
		this.buildTaskToComplete(
			'Insurance amount',
			isSet(matter?.fireInsurance?.coverageAmount),
			UrlService.Matter_Property_FireInsurance(matter?.id),
		);

	fireInsurance_activeDate = (matter: Matter) =>
		this.buildTaskToComplete(
			'Insurance active date',
			isSet(matter?.fireInsurance?.activeDate),
			UrlService.Matter_Property_FireInsurance(matter?.id),
		);

	fireInsurance_broker = (matter: Matter) =>
		this.buildTaskToComplete(
			'Broker',
			isSet(matter?.fireInsurance?.fireInsuranceBrokerage?.name),
			UrlService.Matter_Property_FireInsurance(matter?.id),
		);

	// ------------------------
	// MATTER MORTGAGE
	// ------------------------

	matterMortgage_regNumber = (matterMortgage: MatterMortgage) =>
		this.buildTaskToComplete(
			'Registration number',
			isSet(matterMortgage?.registrationNumber),
		);

	matterMortgage_regDate = (matterMortgage: MatterMortgage) =>
		this.buildTaskToComplete(
			'Registration date',
			isSet(matterMortgage?.registrationDate),
		);

	buildTaskToComplete = (
		title: string,
		completed: boolean,
		url?: string,
		queryParams?: { [id: string]: string },
		addBackButton = true,
	): StepMilestoneTask => {
		let _queryParams = { ...queryParams };

		if (addBackButton) {
			if (queryParams) {
				_queryParams['back-string'] = 'mortgage';
			} else {
				_queryParams = {
					'back-string': 'mortgage',
				};
			}
		}

		return {
			title,
			completed,
			url,
			queryParams: _queryParams,
		};
	};
}
